import { useState } from "react";
import { Dumbbell, Stethoscope, Store, Sparkles, Home, GraduationCap, Church,
  Building, Heart, Star, Users, Briefcase, Coffee, ShoppingBag, Utensils } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ManageTargetsDialog } from "@/components/marketing/ManageTargetsDialog";
import { AddContactDialog } from "@/components/marketing/AddContactDialog";
import { LeadDetailDialog } from "@/components/marketing/LeadDetailDialog";
import { PhoneLink, EmailLink, AddressLink } from "@/components/marketing/contact-links";

const iconMap = { Dumbbell, Stethoscope, Store, Sparkles, Home, GraduationCap, Church,
  Building, Heart, Star, Users, Briefcase, Coffee, ShoppingBag, Utensils };

export function TargetsSection(props) {
  const { targets, subtypes, getSubtypesForTarget, getLeadsForTarget } = props;
  const [activeTarget, setActiveTarget] = useState(targets[0]?.id || "");
  const [activeSubtype, setActiveSubtype] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);

  if (targets.length > 0 && !targets.find((t) => t.id === activeTarget)) {
    setActiveTarget(targets[0].id);
    setActiveSubtype(null);
  }

  const handleTargetChange = (id) => { setActiveTarget(id); setActiveSubtype(null); };
  const filteredLeads = activeSubtype ? getLeadsForTarget(activeTarget, activeSubtype) : getLeadsForTarget(activeTarget);
  const activeTargetSubtypes = getSubtypesForTarget(activeTarget);

  const handleLeadClick = (lead) => { setSelectedLead(lead); setLeadDetailOpen(true); };

  return (
    <Card className="card-shadow border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold">Targets</CardTitle>
          <ManageTargetsDialog {...props} />
        </div>
        <div className="mt-2">
          <AddContactDialog targets={targets} subtypes={subtypes}
            getSubtypesForTarget={getSubtypesForTarget} onAddLead={props.onAddLead}
            preselectedTargetId={activeTarget} preselectedSubtypeId={activeSubtype} context="targets" />
        </div>
      </CardHeader>
      <CardContent>
        {targets.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No targets configured. Click the settings icon to add targets.</p>
        ) : (
          <Tabs value={activeTarget} onValueChange={handleTargetChange} className="w-full">
            <TabsList className="grid w-full mb-4 h-auto" style={{ gridTemplateColumns: `repeat(${Math.min(targets.length, 7)}, 1fr)` }}>
              {targets.map((target) => {
                const Icon = iconMap[target.icon] || Store;
                const count = getLeadsForTarget(target.id).length;
                return (
                  <TabsTrigger key={target.id} value={target.id} data-testid={`target-tab-${target.id}`}
                    className="flex flex-col gap-1 py-2 px-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground min-h-[70px]">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs hidden sm:inline leading-tight">{target.name}</span>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">{count}</Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {targets.map((target) => (
              <TabsContent key={target.id} value={target.id} className="mt-0">
                <div className="mb-4 pt-2">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3">Filter by Sub-type:</h3>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={activeSubtype === null ? "default" : "outline"} className="cursor-pointer" onClick={() => setActiveSubtype(null)}>All</Badge>
                    {activeTargetSubtypes.map((s) => (
                      <Badge key={s.id} variant={activeSubtype === s.id ? "default" : "outline"} className="cursor-pointer" onClick={() => setActiveSubtype(s.id)}>{s.name}</Badge>
                    ))}
                  </div>
                </div>
                {filteredLeads.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No contacts yet {activeSubtype ? "for this sub-type" : `in ${target.name}`}</p>
                    <p className="text-sm mt-1">Click "Add Contact" to start adding contacts</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="leads-list">
                    {filteredLeads.map((lead) => {
                      const subtype = subtypes.find((s) => s.id === lead.subtype_id);
                      return (
                        <div key={lead.id} data-testid={`lead-${lead.id}`}
                          className="p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors cursor-pointer"
                          onClick={() => handleLeadClick(lead)}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium">{lead.business_name}</h4>
                              <p className="text-sm text-muted-foreground">{lead.contact_person}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {lead.is_target && <Badge className="text-[10px] px-1.5 py-0 h-4 bg-blue-500/15 text-blue-600 border-0">Target</Badge>}
                                {lead.is_event && <Badge className="text-[10px] px-1.5 py-0 h-4 bg-purple-500/15 text-purple-600 border-0">Event</Badge>}
                                {lead.is_catering && <Badge className="text-[10px] px-1.5 py-0 h-4 bg-orange-500/15 text-orange-600 border-0">Catering</Badge>}
                              </div>
                            </div>
                            {subtype && <Badge variant="outline" className="text-xs">{subtype.name}</Badge>}
                          </div>
                          <div className="mt-2 text-xs space-y-1">
                            <PhoneLink phone={lead.phone} className="text-xs" />
                            <EmailLink email={lead.email} className="text-xs" />
                            <AddressLink address={lead.address} className="text-xs truncate" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>

      <LeadDetailDialog lead={selectedLead} open={leadDetailOpen} onOpenChange={setLeadDetailOpen}
        targets={targets} subtypes={subtypes} getSubtypesForTarget={getSubtypesForTarget}
        onUpdate={props.onUpdateLead} onDelete={props.onDeleteLead} />
    </Card>
  );
}
